import { twoHashes, type Key } from "./hashing";

export interface CountMinSketchOptions {
  /** Number of counters per row (columns). */
  width: number;
  /** Number of rows (independent hash functions). */
  depth: number;
  /**
   * Conservative update ("minimal increment"): only raise the counters that
   * currently hold the minimum. Keeps the same no-underestimate guarantee while
   * reducing overestimation, at the cost of no longer supporting decrements.
   */
  conservative?: boolean;
  seed?: number;
}

/**
 * A Count-Min Sketch estimates how many times each item has appeared in a
 * stream, using a fixed 2-D grid of counters. Each of the d rows hashes items
 * to one of w columns and adds to that counter; the estimate for an item is the
 * *minimum* across its d counters. Collisions can only push a counter up, so
 * the estimate never undercounts — it can only overestimate, and by a bounded
 * amount: at most ε·N (N = total count) with probability 1 − δ, where
 * w ≈ e/ε and d ≈ ln(1/δ).
 */
export class CountMinSketch {
  readonly width: number;
  readonly depth: number;
  readonly seed: number;
  readonly conservative: boolean;
  readonly table: Uint32Array;
  totalCount = 0;

  constructor(options: CountMinSketchOptions) {
    const { width, depth, conservative = false, seed = 0 } = options;
    if (width <= 0 || depth <= 0) throw new RangeError("width and depth must be positive");
    this.width = width;
    this.depth = depth;
    this.conservative = conservative;
    this.seed = seed;
    this.table = new Uint32Array(width * depth);
  }

  /**
   * Build a sketch guaranteeing additive error ≤ ε·N with probability ≥ 1 − δ:
   *   width = ceil(e/ε),  depth = ceil(ln(1/δ)).
   */
  static create(
    epsilon: number,
    delta: number,
    options: Omit<CountMinSketchOptions, "width" | "depth"> = {},
  ): CountMinSketch {
    if (epsilon <= 0 || epsilon >= 1) throw new RangeError("epsilon must be in (0, 1)");
    if (delta <= 0 || delta >= 1) throw new RangeError("delta must be in (0, 1)");
    const width = Math.ceil(Math.E / epsilon);
    const depth = Math.ceil(Math.log(1 / delta));
    return new CountMinSketch({ ...options, width, depth });
  }

  private columns(key: Key): number[] {
    const [h1, h2] = twoHashes(key, this.seed);
    const cols = new Array<number>(this.depth);
    for (let r = 0; r < this.depth; r++) cols[r] = (h1 + r * h2) % this.width;
    return cols;
  }

  add(key: Key, count = 1): void {
    const cols = this.columns(key);
    if (this.conservative) {
      let est = Infinity;
      for (let r = 0; r < this.depth; r++) {
        est = Math.min(est, this.table[r * this.width + cols[r]]);
      }
      const target = est + count;
      for (let r = 0; r < this.depth; r++) {
        const idx = r * this.width + cols[r];
        if (this.table[idx] < target) this.table[idx] = target;
      }
    } else {
      for (let r = 0; r < this.depth; r++) this.table[r * this.width + cols[r]] += count;
    }
    this.totalCount += count;
  }

  /** Estimated frequency of a key (never less than the true frequency). */
  estimate(key: Key): number {
    const cols = this.columns(key);
    let min = Infinity;
    for (let r = 0; r < this.depth; r++) {
      min = Math.min(min, this.table[r * this.width + cols[r]]);
    }
    return min;
  }

  /** The (row, column, counter) cells a key touches — used to visualise it. */
  cells(key: Key): Array<{ row: number; col: number; count: number }> {
    const cols = this.columns(key);
    return cols.map((col, row) => ({ row, col, count: this.table[row * this.width + col] }));
  }

  /** The additive error ε·N implied by the current width and total count. */
  errorBound(): number {
    return (Math.E / this.width) * this.totalCount;
  }

  /** Probability the ε·N bound holds for a given query, 1 − e^{-depth}. */
  confidence(): number {
    return 1 - Math.exp(-this.depth);
  }

  /** Counter memory in bytes (4 bytes per counter). */
  memoryBytes(): number {
    return this.table.length * 4;
  }
}
